import { Dropcursor, Gapcursor, UndoRedo } from '@tiptap/extensions'
import Bold from '@tiptap/extension-bold'
import Document from '@tiptap/extension-document'
import HardBreak from '@tiptap/extension-hard-break'
import Heading from '@tiptap/extension-heading'
import Italic from '@tiptap/extension-italic'
import { BulletList, ListItem, OrderedList } from '@tiptap/extension-list'
import Paragraph from '@tiptap/extension-paragraph'
import Strike from '@tiptap/extension-strike'
import { Table, TableCell, TableHeader, TableRow } from '@tiptap/extension-table'
import Text from '@tiptap/extension-text'
import Underline from '@tiptap/extension-underline'
import { NodeSelection } from '@tiptap/pm/state'
import { TableMap } from '@tiptap/pm/tables'

import { cellShading, contextualSpacingOf, isFloatingTable, NEW_TABLE_CSS, normalizeRunColor, PARAGRAPH_ALIGNMENTS, pPrToCss, shadingTone, tblGridCols, tblPrToCss, tblStyleInfo, tcBorderEdges, tcPrToCss, trHeightCss } from '../ooxmlCss.js'
import { headingStyleId } from '../studioContext.js'
import { StudioClipboard } from './studioClipboard.js'
import { StudioPaste } from './studioPaste.js'
import { OoxmlRun, StudioLink } from './studioMarks.js'
import { GRID_GUARDED_COMMANDS, inGridSkippingTable, studioTableEditing } from './tableGrid.js'
import { TableRegions } from './tableRegions.js'
import { TextRuns } from './textRuns.js'
import {
  FootnoteRef,
  PageBreak,
  PageField,
  RawBlock,
  RawInline,
  RawMarker,
  RawRun,
  Tab,
  TemplateBlock,
  TemplateCode,
  TemplateImage,
} from './studioNodes.js'

/**
 * The Studio schema: every importer-emitted attr is DECLARED here — Tiptap
 * silently drops undeclared attrs on Node.fromJSON, which would destroy the
 * round-trip. tests/js/templateStudio/schemaRoundTrip.test.js pins this.
 *
 * Presentation comes from the passthrough attrs via ooxmlCss (the display
 * half of the property-passthrough model); the attrs themselves are opaque
 * and never edited client-side. Edits are delta attrs next to them
 * (textAlign, fill, the ooxmlRun colour/highlight — RED-317 PR 3 contract,
 * .claude/tickets/RED-317/pr3-contract.md), null = untouched, and ooxmlCss
 * renders them the way the compiler writes them. Two view-only decoration
 * plugins come with the schema because every canvas needs them: TextRuns
 * (Word's line model) and TableRegions (cell edges and table-style regions).
 * The others (pagination, list numbering, tab stops) and the ${code}
 * suggestion are added by the editing UI that needs them.
 *
 * Pasted HTML reaches the deltas through `data-ooxml-*` attributes only,
 * which the Word paste transform (../wordPaste.js) writes; HTML from
 * elsewhere keeps its structure and b/i/u/s, never its colours. Copy and
 * paste inside the Studio bypasses HTML altogether (StudioClipboard).
 */

const StudioDocument = Document.extend({
  addAttributes() {
    return {
      sectPr: { default: null, rendered: false },
    }
  },

  addCommands() {
    return {
      /**
       * Moves the selected block (a locked chart card, a table selected as a
       * node) one block up (-1) or down (1) among its siblings, past the
       * invisible bookmark markers between them; it stays selected.
       */
      moveSelectedBlock:
        (direction) =>
        ({ state, tr }) => {
          const { selection } = state
          if (!(selection instanceof NodeSelection) || !selection.node.isBlock) return false
          const $pos = state.doc.resolve(selection.from)
          const parent = $pos.parent
          const step = direction < 0 ? -1 : 1
          let target = $pos.index() + step
          while (target >= 0 && target < parent.childCount && parent.child(target).type.name === 'rawMarker') target += step
          if (target < 0 || target >= parent.childCount) return false
          let targetPos = $pos.start()
          for (let index = 0; index < target; index++) targetPos += parent.child(index).nodeSize
          const node = selection.node
          const insertAt = step < 0 ? targetPos : targetPos + parent.child(target).nodeSize - node.nodeSize
          tr.delete(selection.from, selection.to).insert(insertAt, node)
          tr.setSelection(NodeSelection.create(tr.doc, insertAt)).scrollIntoView()
          return true
        },
    }
  },
})

const paragraphAttributes = {
  pPr: { default: null, rendered: false },
  listNumId: { default: null, rendered: false },
  listIlvl: { default: null, rendered: false },
  // Importer-derived nesting depth of a list item's paragraph (0 =
  // outermost): the compiler shifts ilvl by how far the editor moved the
  // item (contract §3). Never set client-side.
  listDepth: { default: null, rendered: false },
  styleId: { default: null, rendered: false },
  sectionBreak: { default: null, rendered: false },
  // 'left'|'center'|'right'|'justify' → w:jc (contract §2).
  textAlign: {
    default: null,
    rendered: false,
    parseHTML: (element) => {
      const align = element.getAttribute('data-ooxml-align')
      return PARAGRAPH_ALIGNMENTS.includes(align) ? align : null
    },
  },
}

/**
 * p / h1–h6 with the pPr presentation and the alignment delta; data-fill-tone
 * when the paragraph's own shading turns Word's automatic text colour white
 * (or back to black, studio.css).
 */
function renderParagraph(tag, node) {
  const style = pPrToCss(node.attrs.pPr, { textAlign: node.attrs.textAlign })
  const attrs = {
    'data-style-id': node.attrs.styleId ?? undefined,
    'data-contextual-spacing': contextualSpacingOf(node.attrs.pPr) ?? undefined,
    'data-fill-tone': shadingTone(node.attrs.pPr) ?? undefined,
  }
  if (style) attrs.style = style
  return [tag, attrs, 0]
}

const StudioParagraph = Paragraph.extend({
  addAttributes() {
    return { ...paragraphAttributes }
  },

  addCommands() {
    return {
      ...this.parent?.(),
      /**
       * Alignment of every paragraph and heading in the selection (one of
       * PARAGRAPH_ALIGNMENTS; null clears the delta, the pPr's own w:jc shows again).
       */
      setParagraphAlign:
        (align) =>
        ({ commands }) => {
          if (align !== null && !PARAGRAPH_ALIGNMENTS.includes(align)) return false
          const paragraphs = commands.updateAttributes('paragraph', { textAlign: align })
          const headings = commands.updateAttributes('heading', { textAlign: align })
          return paragraphs || headings
        },
    }
  },

  renderHTML({ node }) {
    return renderParagraph('p', node)
  },
})

const StudioHeading = Heading.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      ...paragraphAttributes,
      // A pasted <h3> shows in the fragment's own heading style at once; the
      // compiler gives it that pStyle (contract §2) and the reload agrees.
      styleId: {
        default: null,
        rendered: false,
        parseHTML: (element) => (/^H[1-6]$/.test(element.tagName) ? headingStyleId(Number(element.tagName[1])) : null),
      },
    }
  },

  addCommands() {
    return {
      ...this.parent?.(),
      /**
       * "Alapealkiri": the paragraphs in the selection become headings of
       * `level` in the fragment's style for it, or back to paragraphs when
       * they all are such headings already. Their pPr and deltas stay (the
       * compiler swaps only the pStyle, contract §2). Refuses when the
       * fragment has no style for the level: the heading would re-import as
       * a paragraph.
       */
      toggleSubheading:
        (level) =>
        ({ state, tr }) => {
          const blocks = []
          state.doc.nodesBetween(state.selection.from, state.selection.to, (node, pos) => {
            if (node.type.name === 'paragraph' || node.type.name === 'heading') {
              blocks.push({ node, pos })
              return false
            }
            return true
          })
          if (!blocks.length) return false
          const back = blocks.every(({ node }) => node.type.name === 'heading' && node.attrs.level === level)
          const styleId = back ? null : headingStyleId(level)
          if (!back && styleId === null) return false
          const target = back ? state.schema.nodes.paragraph : state.schema.nodes.heading
          let changed = false
          for (const { node, pos } of blocks) {
            const $pos = state.doc.resolve(pos)
            if (!$pos.parent.canReplaceWith($pos.index(), $pos.index() + 1, target)) continue
            const attrs = Object.fromEntries(Object.keys(paragraphAttributes).map((name) => [name, node.attrs[name] ?? null]))
            tr.setNodeMarkup(pos, target, back ? { ...attrs, styleId: null } : { ...attrs, styleId, level })
            changed = true
          }
          return changed
        },
    }
  },

  renderHTML({ node }) {
    return renderParagraph('h' + node.attrs.level, node)
  },
})

const listAttributes = {
  addAttributes() {
    return {
      numId: { default: null, rendered: false },
    }
  },
}

const StudioBulletList = BulletList.extend(listAttributes)
const StudioOrderedList = OrderedList.extend(listAttributes)

const StudioTable = Table.extend({
  addAttributes() {
    return {
      tblPr: { default: null, rendered: false },
      tblGrid: { default: null, rendered: false },
      // Bookmark elements trailing the last row, verbatim (M8.1).
      markersAfter: { default: null, rendered: false },
    }
  },

  // The base extension installs a TableView node view that builds its own
  // <table> DOM and bypasses renderHTML (class + tblPr styles lost). The
  // studio renders tables straight from renderHTML instead.
  addNodeView() {
    return null
  },

  // A table whose rows skip grid columns (w:gridBefore/w:gridAfter) keeps
  // its rows as they are: no fix-up cells, no structure commands (tableGrid.js).
  addProseMirrorPlugins() {
    return [studioTableEditing({ allowTableNodeSelection: this.options.allowTableNodeSelection })]
  },

  addCommands() {
    const parent = this.parent?.() ?? {}
    const guarded = Object.fromEntries(
      GRID_GUARDED_COMMANDS.filter((name) => parent[name]).map((name) => [
        name,
        (...args) =>
          (props) =>
            !inGridSkippingTable(props.state) && parent[name](...args)(props),
      ]),
    )
    return { ...parent, ...guarded }
  },

  renderHTML({ node }) {
    const isNew = node.attrs.tblPr === null && node.attrs.tblGrid === null
    let style = isNew ? NEW_TABLE_CSS : node.attrs.tblPr ? tblPrToCss(node.attrs.tblPr) : ''
    const cols = columnWidths(node)
    // A floating table of automatic width is as wide as its grid in Word; a
    // CSS float of auto width would shrink-wrap its longest line instead.
    if (isFloatingTable(node.attrs.tblPr) && !style.includes('--sb-width')) {
      const gridPt = tblGridCols(node.attrs.tblGrid).reduce((sum, width) => sum + width, 0)
      if (gridPt > 0) style = [style, `--sb-width: ${gridPt}pt`].filter(Boolean).join('; ')
    }
    const colgroup = cols.length ? [['colgroup', {}, ...cols.map((width) => ['col', width ? { style: `width: ${width}` } : {}])]] : []
    const styleInfo = node.attrs.tblPr ? tblStyleInfo(node.attrs.tblPr) : null
    return [
      'table',
      {
        class: 'studio-table',
        style: style || undefined,
        'data-tstyle': styleInfo?.styleId ?? undefined,
      },
      ...colgroup,
      ['tbody', 0],
    ]
  },
})

const StudioTableRow = TableRow.extend({
  addAttributes() {
    return {
      trPr: { default: null, rendered: false },
      // Bookmark elements that sat between rows, re-emitted before this
      // row's <w:tr> at compile time (M8.1).
      markersBefore: { default: null, rendered: false },
    }
  },

  renderHTML({ node }) {
    const height = trHeightCss(node.attrs.trPr)
    return ['tr', height ? { style: height } : {}, 0]
  },
})

const cellAttributes = {
  tcPr: { default: null, rendered: false },
  vMerge: { default: null, rendered: false },
  // 6-hex → w:shd fill, 'auto' → no fill (contract §4; setCellAttribute('fill', …)).
  fill: {
    default: null,
    rendered: false,
    parseHTML: (element) => {
      const fill = normalizeRunColor(element.getAttribute('data-ooxml-fill'))
      return fill === 'auto' ? null : fill
    },
  },
}

/**
 * td / th. A rowspan only comes from the editor (mergeCells): imported
 * tables keep their real vMerge continuation cells, rowspan 1. A
 * continuation draws the shading of the cell it continues, as Word does
 * (TableRegions decorates it, tableRegions.js), never its own. The tone of
 * the cell's own shading (data-fill-tone) sets Word's automatic text colour
 * in it over a table style's region fill (studio.css). data-tc-edges: the
 * edges the cell's tcBorders names, which a table style's region border
 * then leaves alone (studioStylesheets.js).
 */
function renderCell(tag, node) {
  const continues = node.attrs.vMerge === 'continue'
  const style = tcPrToCss(node.attrs.tcPr, { fill: node.attrs.fill, shading: !continues })
  const tone = continues ? null : cellShading(node.attrs.tcPr, node.attrs.fill).tone
  return [
    tag,
    {
      colspan: node.attrs.colspan !== 1 ? node.attrs.colspan : undefined,
      rowspan: node.attrs.rowspan !== 1 ? node.attrs.rowspan : undefined,
      'data-vmerge': node.attrs.vMerge ?? undefined,
      'data-fill-tone': tone ?? undefined,
      'data-tc-edges': tcBorderEdges(node.attrs.tcPr) || undefined,
      style: style || undefined,
    },
    0,
  ]
}

const StudioTableCell = TableCell.extend({
  addAttributes() {
    return { ...this.parent?.(), ...cellAttributes }
  },

  renderHTML({ node }) {
    return renderCell('td', node)
  },
})

/**
 * A cell of a repeated header row: the same attrs as a tableCell. A row
 * whose cells are all tableHeader compiles with w:tblHeader (toggleHeaderRow);
 * the importer types the cells of an active w:tblHeader row this way.
 */
const StudioTableHeader = TableHeader.extend({
  addAttributes() {
    return { ...this.parent?.(), ...cellAttributes }
  },

  renderHTML({ node }) {
    return renderCell('th', node)
  },
})

/**
 * The table's <col> widths as CSS lengths ('' = no width), matching the
 * tblGrid the compiler writes (contract §4): an imported table keeps its
 * tblGrid; one whose column count the editor changed gets an even split of
 * its previous total width (unless a row skips grid columns with
 * gridBefore/gridAfter — then the cell count is not the grid's); a new table
 * splits the text width in proportion to the first row's colwidth when every
 * column has one, else evenly (no widths under table-layout: fixed).
 */
function columnWidths(node) {
  const columns = TableMap.get(node).width
  if (node.attrs.tblGrid === null) {
    const weights = []
    node.firstChild?.forEach((cell) => {
      for (let index = 0; index < cell.attrs.colspan; index++) weights.push(Number(cell.attrs.colwidth?.[index]) || 0)
    })
    const total = weights.reduce((sum, weight) => sum + weight, 0)
    return weights.length === columns && weights.every((weight) => weight > 0) ? weights.map((weight) => `${(weight / total) * 100}%`) : []
  }

  const grid = tblGridCols(node.attrs.tblGrid)
  let skipsGridColumns = false
  node.forEach((row) => {
    skipsGridColumns ||= /<w:grid(?:Before|After)\b/.test(row.attrs.trPr ?? '')
  })
  if (grid.length && columns > 0 && grid.length !== columns && !skipsGridColumns) {
    const total = grid.reduce((sum, width) => sum + width, 0)
    return Array.from({ length: columns }, () => `${total / columns}pt`)
  }
  return grid.map((width) => (width > 0 ? `${width}pt` : ''))
}

const StudioHardBreak = HardBreak.extend({
  addAttributes() {
    return {
      rPr: { default: null, rendered: false },
    }
  },
})

/**
 * @param {{ paste?: object }} [options] paste: StudioPaste's options (the
 *   picture uploader and the notice callback of the editing UI).
 */
export function buildStudioExtensions(options = {}) {
  return [
    StudioDocument,
    Text,
    StudioParagraph,
    StudioHeading,
    StudioBulletList,
    StudioOrderedList,
    ListItem,
    StudioTable,
    StudioTableRow,
    StudioTableCell,
    StudioTableHeader,
    StudioHardBreak,
    Bold,
    Italic,
    Underline,
    Strike,
    OoxmlRun,
    StudioLink,
    TemplateCode,
    TemplateBlock,
    RawBlock,
    RawInline,
    RawMarker,
    RawRun,
    FootnoteRef,
    Tab,
    PageBreak,
    PageField,
    TemplateImage,
    UndoRedo,
    Dropcursor,
    Gapcursor,
    StudioClipboard,
    StudioPaste.configure(options.paste ?? {}),
    TextRuns,
    TableRegions,
  ]
}
